import { useState, useEffect } from 'react';
import { Button } from './ui-common/button';
import { Input } from './ui-common/input';
import { UserCog, X } from 'lucide-react';
import axiosInstance from './axiosInstance';

const ImpersonationButton = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [targetEmail, setTargetEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [impersonationStatus, setImpersonationStatus] = useState(null);

  useEffect(() => {
    // Only fetch status if user is admin
    if (user?.is_admin) {
      fetchImpersonationStatus();
    }
  }, [user?.is_admin]);

  const fetchImpersonationStatus = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/impersonate/status');
      setImpersonationStatus(response.data);
    } catch (err) {
      console.error('Error fetching impersonation status:', err);
      setImpersonationStatus({ is_impersonating: false });
    }
  };

  const handleStartImpersonation = async () => {
    if (!targetEmail || !targetEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/api/admin/impersonate', {
        user_email: targetEmail,
      });

      if (response.status === 200) {
        // Close dialog immediately
        setIsOpen(false);

        // Wait a bit longer for session to be fully saved, then do a full page reload
        // Using replace instead of href to avoid history issues
        setTimeout(() => {
          window.location.replace('/');
        }, 250);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start impersonation');
      setIsLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/api/admin/impersonate/stop');

      if (response.status === 200) {
        // Wait a bit longer for session to be restored, then do a full page reload
        setTimeout(() => {
          window.location.replace('/');
        }, 250);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to stop impersonation');
      setIsLoading(false);
    }
  };

  // Don't show if not admin
  if (!user?.is_admin) {
    return null;
  }

  // Show stop button if impersonating
  if (impersonationStatus?.is_impersonating) {
    return (
      <div className="flex items-center gap-2">
        <div className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-2 py-1 rounded text-xs">
          Impersonating: {impersonationStatus.impersonated_user_email}
        </div>
        <Button variant="outline" size="sm" onClick={handleStopImpersonation} disabled={isLoading}>
          Stop
        </Button>
      </div>
    );
  }

  // Show impersonate button
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
        <UserCog className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20" onClick={() => setIsOpen(false)}>
          <div className="bg-background border rounded-lg shadow-lg p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Impersonate User</h3>
              <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">User Email</label>
                <Input
                  type="email"
                  placeholder="student@mail.tau.ac.il"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStartImpersonation()}
                  className="mt-1"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  {error}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
                  Cancel
                </Button>
                <Button onClick={handleStartImpersonation} disabled={isLoading || !targetEmail}>
                  {isLoading ? 'Starting...' : 'Start'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImpersonationButton;

